import { Box, Button, Flex, Text } from '@rocket/ui';
import { diffWordsWithSpace } from 'diff';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LuChevronLeft,
  LuChevronRight,
  LuRefreshCw,
  LuThumbsDown,
  LuThumbsUp,
} from 'react-icons/lu';
import { useFtueHost } from '../host';
import { getRuleExamples, type RuleExample, removeRuleExample } from '../onboarding-api';

const FXI_TRAIN_TYPES = new Set(['fsi', 'fpi', 'fci']);

interface Props {
  ruleId: string;
  onDismiss: () => void;
  context?: 'review' | 'copy';
}

export function RuleExampleCarousel({ ruleId, onDismiss, context = 'review' }: Props) {
  const { bbAxios } = useFtueHost();
  const [examples, setExamples] = useState<Array<RuleExample>>([]);
  const [index, setIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [noMoreAvailable, setNoMoreAvailable] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const fetch = async (nextOffset: number, initial = false) => {
    if (initial) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      const result = await getRuleExamples(bbAxios, ruleId, nextOffset, 3);
      if (result.examples.length === 0) {
        if (nextOffset === 0) {
          setExamples([]);
        } else {
          // Wrapped around: no more new examples
          setNoMoreAvailable(true);
        }
      } else {
        setExamples(result.examples);
        setTotalCount(result.totalCount);
        setOffset(nextOffset);
        setIndex(0);
        setNoMoreAvailable(false);
      }
      return result.examples.length;
    } catch {
      // silently ignore — carousel will remain as-is
      return -1;
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    retryCountRef.current = 0;
    const load = async () => {
      const count = await fetch(0, true);
      if (count === 0) {
        // Background enhancement may not have finished yet — poll a few times
        const poll = () => {
          retryCountRef.current += 1;
          if (retryCountRef.current > 6) {
            return;
          }
          retryRef.current = setTimeout(async () => {
            const n = await fetch(0, true);
            if (n === 0 && retryCountRef.current < 6) {
              poll();
            }
          }, 3000);
        };
        poll();
      }
    };
    void load();
    return () => {
      if (retryRef.current) {
        clearTimeout(retryRef.current);
      }
    };
  }, [ruleId]);

  const handleReject = async (tuId: string) => {
    setIsRemoving(true);
    try {
      await removeRuleExample(bbAxios, ruleId, tuId);
      const remaining = examples.filter((e) => e.tuId !== tuId);
      setExamples(remaining);
      setTotalCount((c) => Math.max(0, c - 1));
      if (index >= remaining.length && remaining.length > 0) {
        setIndex(remaining.length - 1);
      }
    } catch {
      // silently ignore
    } finally {
      setIsRemoving(false);
    }
  };

  const handleDifferentExamples = () => {
    const nextOffset = offset + 3 < totalCount ? offset + 3 : 0;
    void fetch(nextOffset);
  };

  const current = examples[index];
  const isFxi = current ? FXI_TRAIN_TYPES.has((current.trainType ?? '').toLowerCase()) : false;

  const diffParts = useMemo(
    () => (current ? diffWordsWithSpace(current.before, current.after) : []),
    [current]
  );

  const isPolling =
    examples.length === 0 && retryCountRef.current > 0 && retryCountRef.current <= 6;

  if (isLoading || isPolling) {
    return (
      <Box
        mt="1"
        p="2"
        bg="bg.subtle"
        borderWidth="1px"
        borderRadius="sm"
        borderColor="border.muted"
      >
        <Text fontSize="xs" color="fg.muted">
          Loading examples…
        </Text>
      </Box>
    );
  }

  if (examples.length === 0) {
    return null;
  }

  return (
    <Box mt="1" p="2" bg="bg.subtle" borderWidth="1px" borderRadius="sm" borderColor="border.muted">
      {/* Header row */}
      <Flex.H align="center" justify="space-between" mb="1">
        <Text fontSize="xs" color="fg.subtle" whiteSpace="nowrap">
          {examples.length > 1 ? `Example ${index + 1} of ${examples.length}` : 'Example edit'}
        </Text>
        {examples.length > 1 && (
          <Flex.H align="center" gap="1">
            <Button
              size="xs"
              variant="ghost"
              disabled={index === 0}
              onClick={() => setIndex((i) => i - 1)}
              aria-label="Previous example"
            >
              <LuChevronLeft size={12} />
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={index === examples.length - 1}
              onClick={() => setIndex((i) => i + 1)}
              aria-label="Next example"
            >
              <LuChevronRight size={12} />
            </Button>
          </Flex.H>
        )}
      </Flex.H>

      {/* Diff view */}
      {isFxi && current?.contextBefore && (
        <Text fontSize="xs" color="fg.subtle" fontStyle="italic" mb="1">
          Insert after: &ldquo;{current.contextBefore}&rdquo;
        </Text>
      )}
      <Text fontSize="xs" lineHeight="tall">
        {diffParts.map(({ added, removed, value }: (typeof diffParts)[number], i: number) => (
          <Text
            key={i}
            as="span"
            color={added ? 'green.fg' : removed ? 'red.fg' : undefined}
            fontWeight={added ? 'bold' : undefined}
            textDecoration={removed ? 'line-through' : undefined}
          >
            {value}
          </Text>
        ))}
      </Text>
      {isFxi && current?.contextFollowing && (
        <Text fontSize="xs" color="fg.subtle" fontStyle="italic" mt="1">
          Insert before: &ldquo;{current.contextFollowing}&rdquo;
        </Text>
      )}

      {/* Actions */}
      <Flex.H align="center" justify="flex-end" gap="2" mt="2">
        {context === 'review' && current && (
          <Button
            size="xs"
            variant="ghost"
            colorPalette="red"
            disabled={isRemoving}
            onClick={() => void handleReject(current.tuId)}
            aria-label="Reject this example"
          >
            <LuThumbsDown size={12} />
            Bad match
          </Button>
        )}
        {context === 'review' && !noMoreAvailable && totalCount > 3 && (
          <Button
            size="xs"
            variant="ghost"
            disabled={isRefreshing}
            loading={isRefreshing}
            onClick={handleDifferentExamples}
            aria-label="Show different examples"
          >
            <LuRefreshCw size={12} />
            Different
          </Button>
        )}
        <Button
          size="xs"
          variant="ghost"
          colorPalette="green"
          onClick={onDismiss}
          aria-label="Looks good"
        >
          <LuThumbsUp size={12} />
          Looks good
        </Button>
      </Flex.H>
    </Box>
  );
}
